import { CustomerDTO, OrganizationDTO, UserDTO, UserType } from '@luminovo/api-contracts';
import { isPresent, isProductionEnvironment } from '@luminovo/commons';
import { analytics, initAnalytics, initSentry, toUserTypeTrait } from '@luminovo/data-stack';
import { getHttpClientConfig } from '@luminovo/http-client';

import { ConsentType } from './cookieConsent';
import { initHelpHero } from './helphero';

export type InitializeServicesParams = {
    consentedCookies: ConsentType[];
    user: UserDTO;
    organization: OrganizationDTO;
    customer: CustomerDTO | null;
    userType: UserType;
    hasFullManufacturingAccess: boolean;
    hasAccessToSourceToContract: boolean;
    tenantName: string;
};

/** `DISABLE_THIRD_PARTY_TRACKING` is for ITAR compliance. */
function shouldEnableThirdPartyServices(): boolean {
    return window.DISABLE_THIRD_PARTY_TRACKING !== 'true' && isProductionEnvironment();
}

/** Callable before sign-in, e.g. in ErrorBoundary; `initializeServices` sets the user context afterwards. */
export function initializeSentry({
    consentedCookies,
    tenantName,
}: {
    consentedCookies: ConsentType[];
    tenantName: string;
}): void {
    if (!shouldEnableThirdPartyServices()) {
        return;
    }
    if (!consentedCookies.includes(ConsentType.support)) {
        return;
    }
    initSentry(tenantName);
}

/** Call once the user is authenticated and their consent is known. */
export function initializeServices({
    consentedCookies,
    user,
    organization,
    customer,
    userType,
    hasFullManufacturingAccess,
    hasAccessToSourceToContract,
    tenantName,
}: InitializeServicesParams): void {
    if (!shouldEnableThirdPartyServices()) {
        return;
    }

    const isInternalUser = user.email.endsWith('@luminovo.ai') || user.email.endsWith('@luminovo.com');

    const userTraits = {
        email: user.email,
        language: user.user_language,
        user_type: toUserTypeTrait(userType),
        is_from_customer: userType === UserType.Customer,
        customer_name: isPresent(customer) ? customer.name : undefined,
        host: getHttpClientConfig().hostName,
        tenant: tenantName,
        has_access_to_customer_portal: organization.has_access_to_customer_portal,
        has_access_to_source_to_contract: hasAccessToSourceToContract,
        is_closed_beta_tester: organization.is_closed_beta_tester,
        has_full_manufacturing_access: hasFullManufacturingAccess,
        has_customers: organization.has_customers,
        client_type: organization.client_type ?? '',
    };

    if (consentedCookies.includes(ConsentType.support)) {
        initSentry(tenantName, { userId: user.id, email: user.email });
    }

    if (!isInternalUser) {
        initHelpHero(user.id, userTraits);
    }

    if (!isInternalUser && consentedCookies.includes(ConsentType.analytics)) {
        initAnalytics();
        analytics.identify(user.id, userTraits);
        analytics.group(tenantName, {
            name: tenantName,
            has_access_to_customer_portal: organization.has_access_to_customer_portal,
            has_access_to_source_to_contract: hasAccessToSourceToContract,
            is_closed_beta_tester: organization.is_closed_beta_tester,
            has_full_manufacturing_access: hasFullManufacturingAccess,
            has_customers: organization.has_customers,
            client_type: organization.client_type ?? '',
        });
    }
}
